import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  FormHelperText,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  TextField,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { AsyncAutocomplete } from '@pro4-erp/core-web';
import { WorkOrderContact } from '../types';
import ContactModal from './ContactModal';
import { getMessageFromError } from '@/core';
import { updateWOContact } from '../api/updateWOContact';
import { PostOffice, getPostOFfice } from '@/common';

type WorkOrderContactDetail = WorkOrderContact & {
  open?: boolean;
};

type FormType = {
  title: string;
  address: string;
  postOffice: PostOffice;
  employees: number;
  contacts: WorkOrderContactDetail[];
  comment?: string;
};

type FormProps = {
  onSubmit: (data: Partial<FormType>) => void;
  initialState?: FormType;
  hasFormChanged?: (form: FormType) => boolean;
  onUnmount?: (form: FormType) => void;
  isFormEditable?: boolean;
  wo_id: string;
  refetchWo: () => void;
};

const Form = ({
  onSubmit,
  initialState,
  hasFormChanged,
  onUnmount,
  isFormEditable,
  wo_id,
  refetchWo,
}: FormProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [showContactModal, setShowContactModal] = useState(false);
  const [indexContact, setIndexContact] = useState(0);
  const [commentShow, setCommentShow] = useState(false);

  const { mutate } = useMutation({
    mutationFn: updateWOContact,
    onSuccess: () => {
      enqueueSnackbar(
        t('Proposal sent successfully.', { defaultValue: 'Predlog usprešno poslan.' }),
        {
          variant: 'success',
        }
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const form = useForm<FormType>({
    mode: 'onTouched',
    defaultValues: {
      title: '',
      address: '',
      postOffice: { id: '' },
      employees: 0,
      contacts: [],
      comment: '',
    },
  });
  const { watch } = form;
  // const watchedFields = form.watch();
  const [contactsData, setContactsData] = useState<WorkOrderContactDetail[]>(
    form.getValues().contacts
  );

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (value && name && type) setCommentShow(true);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    return () => {
      if (onUnmount) {
        onUnmount(form.getValues());
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    form.reset({
      title: initialState?.title,
      address: initialState?.address,
      postOffice: initialState?.postOffice,
      employees: initialState?.employees,
      contacts: initialState?.contacts,
    });

    setContactsData(initialState?.contacts || []);
  }, [initialState, form]);

  const baseSubmit = (data: FormType) => {
    if (commentShow && !data.comment) {
      form.setError('comment', { message: 'Comment is required', type: 'required' });
      return;
    }
    if (hasFormChanged) {
      if (!hasFormChanged(data)) {
        enqueueSnackbar(t('Form was not changed.', { defaultValue: 'Form was not changed.' }), {
          variant: 'info',
        });
        return;
      }
    }

    onSubmit(data);
  };

  const openModal = () => {
    setShowContactModal(true);
  };

  const handleClick = (id: string) => {
    const newList = contactsData.map((contact) => {
      if (id === contact.id) {
        const tmp = {
          ...contact,
          open: !contact.open,
        };
        return tmp;
      }
      return contact;
    });

    setContactsData(newList);
  };

  const renderExpandButton = (contact: WorkOrderContactDetail) => {
    if (form.getValues().contacts.length && contact.open) {
      return <ExpandLessIcon />;
    }
    if (form.getValues().contacts.length && !contact.open) {
      return <ExpandMoreIcon />;
    }
    return null;
  };

  return (
    <>
      <ContactModal
        title="Kontaktni Podatki"
        isOpen={showContactModal}
        setIsOpen={(open: boolean) => {
          setShowContactModal(open);
        }}
        contact={contactsData[indexContact]}
        wo_id={wo_id}
        refetchWo={refetchWo}
      />

      <Box component="form" onSubmit={form.handleSubmit(baseSubmit)} noValidate>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <Controller
            control={form.control}
            name="title"
            render={({ field }) => (
              <TextField
                {...field}
                label={t('Title', { defaultValue: 'Title' })}
                fullWidth
                disabled={!isFormEditable}
                error={!!form.formState.errors.title}
                helperText={form.formState.errors.title?.message?.toString()}
              />
            )}
          />

          <Controller
            control={form.control}
            name="address"
            render={({ field }) => (
              <TextField
                {...field}
                label={t('Address', { defaultValue: 'Address' })}
                fullWidth
                disabled={!isFormEditable}
                error={!!form.formState.errors.address}
                helperText={form.formState.errors.address?.message?.toString()}
              />
            )}
          />

          <AsyncAutocomplete<PostOffice>
            id="post-office"
            label={t('Post office', { defaultValue: 'Post office' })}
            fullWidth
            getData={async (search) => {
              const response = await getPostOFfice({
                page: 1,
                pageSize: 5,
                search,
              });

              return response.results;
            }}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            selectedOption={form.getValues('postOffice')}
            setSelectedOption={(option) => {
              form.setValue('postOffice', option || { id: '' }, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
            disabled={!isFormEditable}
          />

          <Controller
            control={form.control}
            name="employees"
            render={({ field }) => (
              <TextField
                {...field}
                label={t('Num of employees', { defaultValue: 'Num of employees' })}
                fullWidth
                disabled={!isFormEditable}
                error={!!form.formState.errors.employees}
                helperText={form.formState.errors.employees?.message?.toString()}
              />
            )}
          />
          {commentShow && (
            <Controller
              control={form.control}
              name="comment"
              render={({ field }) => (
                <TextField
                  {...field}
                  type="text"
                  label={t('Comment', { defaultValue: 'Comment' })}
                  fullWidth
                  error={!!form.formState.errors.comment}
                  helperText={form.formState.errors.comment?.message?.toString()}
                />
              )}
            />
          )}
        </Box>

        <List
          sx={{ mt: 5, width: '100%', borderRadius: '4px', boxShadow: 1 }}
          component="div"
          aria-labelledby="offer-list"
          subheader={
            <ListSubheader component="div" id="nested-list-subheader" sx={{ borderRadius: '4px' }}>
              {t('Contacts', { defaultValue: 'Contacts' })}
            </ListSubheader>
          }
        >
          {contactsData.map((contactData, index) => (
            <Box component="div" key={contactData.id}>
              <ListItemButton onClick={() => handleClick(contactData.id)}>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.25rem' }}
                  secondaryTypographyProps={{ fontSize: '1rem' }}
                  primary={contactData.name ?? 'N/A'}
                />
                {renderExpandButton(contactData)}
              </ListItemButton>

              {form.getValues().contacts.length > 0 ? (
                <Collapse
                  in={contactData.open}
                  timeout="auto"
                  unmountOnExit
                  key={`${contactData.id}}`}
                >
                  <List component="div" dense disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => {
                        setIndexContact(index);
                        openModal();
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '1.25rem' }}
                        secondaryTypographyProps={{ fontSize: '1rem' }}
                        primary={t('Name', { defaultValue: 'Name' })}
                        secondary={contactData.name ?? ''}
                      />
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '1.25rem' }}
                        secondaryTypographyProps={{ fontSize: '1rem' }}
                        primary={t('Mail', { defaultValue: 'Mail' })}
                        secondary={contactData.mail ?? ''}
                      />
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '1.25rem' }}
                        secondaryTypographyProps={{ fontSize: '1rem' }}
                        primary={t('Phone', { defaultValue: 'Phone' })}
                        secondary={contactData.tel ?? ''}
                      />
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '1.25rem' }}
                        secondaryTypographyProps={{ fontSize: '1rem' }}
                        primary={t('Mobile', { defaultValue: 'Mobile' })}
                        secondary={contactData.mobile ?? ''}
                      />
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '1.25rem' }}
                        secondaryTypographyProps={{ fontSize: '1rem' }}
                        primary={t('Role', { defaultValue: 'Role' })}
                        secondary={
                          contactData.role
                            ? t(contactData.role, { defaultValue: contactData.role })
                            : ''
                        }
                      />
                      <ListItemText
                        primaryTypographyProps={{ fontSize: '1.25rem' }}
                        primary={<EditIcon />}
                      />
                    </ListItemButton>
                  </List>
                </Collapse>
              ) : null}
            </Box>
          ))}
        </List>

        <Button
          variant="contained"
          fullWidth
          sx={{ mt: '1rem' }}
          onClick={() => {
            setIndexContact(9999);
            openModal();
          }}
        >
          {t('Dodaj kontakt', { defaultValue: 'Dodaj kontakt' })}
        </Button>

        <Button
          variant="contained"
          type="submit"
          fullWidth
          sx={{ mt: '1rem' }}
          disabled={!form.formState.isDirty}
        >
          {t('Submit', { defaultValue: 'Submit' })}
        </Button>

        {!!form.formState.errors.root && (
          <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
        )}
      </Box>
    </>
  );
};

export default Form;
